import { isEmpty, isPresent } from '@ember/utils';
import Model, { attr } from '@ember-data/model';
import { cached } from '@glimmer/tracking';
import type { ContactCustomPropertyType } from 'donor-elf-web/services/contacts-cache';

export interface ContactPhoneEmailItem {
  v: string;
  l?: string;
}
export interface ContactPhoneEmail {
  data?: ContactPhoneEmailItem[];
}

export interface ContactCustomProperty {
  l: string;
  v: any;
  t: ContactCustomPropertyType;
}

interface ContactCustom {
  properties?: ContactCustomProperty[];
}

export default class ContactModel extends Model {
  @attr('string') name?: string;
  @attr('string') mailingName?: string;
  @attr('string') greeting?: string;
  @attr('string') firstName?: string;
  @attr('string') spouseFirstName?: string;
  @attr('string') lastName?: string;
  @attr('string') spouseLastName?: string;
  @attr('string') address1?: string;
  @attr('string') address2?: string;
  @attr('string') city?: string;
  @attr('string') state?: string;
  @attr('string') zip?: string;
  @attr('string') country?: string;
  @attr('boolean') deceased!: boolean;
  @attr('boolean') spouseDeceased!: boolean;
  @attr() phones?: ContactPhoneEmail;
  @attr() emails?: ContactPhoneEmail;
  @attr() groups?: string[];
  @attr() custom?: ContactCustom;
  @attr('boolean') declare active: boolean;
  @attr('number') latitude?: number;
  @attr('number') longitude?: number;
  @attr('number') locationConfidence?: number;
  @attr('boolean') anonymousDonor!: boolean;
  @attr('boolean') subscribeToNewsletter?: boolean; // only used when creating a new contact record

  // Below fields are only used for creating a new contact. they will not be
  // returned from the API
  @attr('string') recommendedByContactId?: string;
  @attr() emailAddressesToSyncWithMailchimp?: string[];
  // If true, it will apply the Mailchimp automatic sync settings for the current user.
  // To be used when importing contacts in bulk. When only creating a single contact
  // it should use `emailAddressesToSyncWithMailchimp` above.
  @attr('boolean') applyMailchimpAutomaticSyncSettings?: boolean;

  get nameLowerCase() {
    return (this.name || '').toLowerCase();
  }

  @cached
  get allNamesLowerCase() {
    return `${(this.name || '').toLowerCase()}${(this.firstName || '').toLowerCase()}${(this.lastName || '').toLowerCase()}${(this.spouseFirstName || '').toLowerCase()}${(this.spouseLastName || '').toLowerCase()}`;
  }

  @cached
  get cityAndState() {
    let { city, state } = this;
    if (isEmpty(city) && isEmpty(state)) {
      return undefined;
    } else if (!isEmpty(city) && !isEmpty(state)) {
      return `${city}, ${state}`;
    } else if (!isEmpty(city)) {
      return city;
    } else {
      return state;
    }
  }

  get deceasedMessage() {
    let { deceased, spouseDeceased, firstName, spouseFirstName } = this;
    if (deceased && spouseDeceased) {
      return 'Deceased';
    } else if (deceased) {
      if (isEmpty(firstName) || isEmpty(spouseFirstName)) {
        return 'Deceased';
      } else {
        return `${firstName} is Deceased`;
      }
    } else if (spouseDeceased) {
      if (isEmpty(spouseFirstName)) {
        return 'Spouse is Deceased';
      } else {
        return `${spouseFirstName} is Deceased`;
      }
    }
    return undefined;
  }

  get hasPhones() {
    return (this.phones?.data?.length || 0) > 0;
  }

  get hasEmails() {
    return (this.emails?.data?.length || 0) > 0;
  }

  @cached
  get sortedGroups() {
    return this.groups?.sort();
  }

  get hasGroups() {
    return isPresent(this.groups);
  }

  get hasCustom() {
    return (this.custom?.properties?.length || 0) > 0;
  }

  @cached
  get customPropertiesByLabel(): { [label: string]: ContactCustomProperty } {
    return (this.custom?.properties || []).reduce(
      (obj: any, x) => ((obj[x.l] = x), obj),
      {},
    );
  }

  customPropertyValue(customPropertyLabel: string) {
    return this.customPropertiesByLabel[customPropertyLabel]?.v;
  }

  // is firstName, spouseFirstName, lastName different than name
  @cached
  get namePartsDifferentThanDisplayName() {
    let { nameLowerCase, firstName, spouseFirstName, lastName } = this;
    let different = false;
    // @ts-ignore
    if (
      isPresent(firstName) &&
      nameLowerCase.indexOf(firstName.toLowerCase()) === -1
    ) {
      different = true;
    }
    // @ts-ignore
    if (
      isPresent(spouseFirstName) &&
      nameLowerCase.indexOf(spouseFirstName.toLowerCase()) === -1
    ) {
      different = true;
    }
    // @ts-ignore
    if (
      isPresent(lastName) &&
      nameLowerCase.indexOf(lastName.toLowerCase()) === -1
    ) {
      different = true;
    }
    return different;
  }

  get hidden() {
    return !this.active;
  }
}

declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    contact: ContactModel;
  }
}

export function generateNameFields(args: {
  firstName?: string;
  lastName?: string;
  spouseFirstName?: string;
  spouseLastName?: string;
}): { name?: string; mailingName?: string; greeting?: string } {
  let names: any = {};

  let firstName = args.firstName?.trim();
  let lastName = args.lastName?.trim();
  let spouseFirstName = args.spouseFirstName?.trim();
  let spouseLastName = args.spouseLastName?.trim();
  if (
    isPresent(firstName) &&
    isPresent(lastName) &&
    isPresent(spouseFirstName) &&
    isPresent(spouseLastName) &&
    spouseLastName !== lastName
  ) {
    names.name = `${lastName}, ${firstName} & ${spouseFirstName} ${spouseLastName}`;
    names.mailingName = `${firstName} ${lastName} & ${spouseFirstName} ${spouseLastName}`;
    names.greeting = `${firstName} & ${spouseFirstName}`;
  } else if (
    isPresent(firstName) &&
    isPresent(lastName) &&
    isPresent(spouseFirstName)
  ) {
    names.name = `${lastName}, ${firstName} & ${spouseFirstName}`;
    names.mailingName = `${firstName} & ${spouseFirstName} ${lastName}`;
    names.greeting = `${firstName} & ${spouseFirstName}`;
  } else if (isPresent(firstName) && isPresent(lastName)) {
    names.name = `${lastName}, ${firstName}`;
    names.mailingName = `${firstName} ${lastName}`;
    names.greeting = `${firstName}`;
  } else if (isPresent(firstName) && isPresent(spouseFirstName)) {
    names.name = `${firstName} & ${spouseFirstName}`;
    names.mailingName = `${firstName} & ${spouseFirstName}`;
    names.greeting = `${firstName} & ${spouseFirstName}`;
  } else if (isPresent(firstName)) {
    names.name = firstName;
    names.mailingName = firstName;
    names.greeting = firstName;
  }

  return names;
}
